export interface Hill {
    x: number
    y: number
    radius: number
    rings: number
    seed: number
    stretch?: number
}

type Point = [number, number]

const format = (value: number): string => value.toFixed(1)

const smoothClosedPath = (points: Point[]): string => {
    const count = points.length
    let d = `M${format(points[0][0])} ${format(points[0][1])}`

    for (let i = 0; i < count; i++) {
        const p0 = points[(i - 1 + count) % count]
        const p1 = points[i]
        const p2 = points[(i + 1) % count]
        const p3 = points[(i + 2) % count]

        const c1x = p1[0] + (p2[0] - p0[0]) / 6
        const c1y = p1[1] + (p2[1] - p0[1]) / 6
        const c2x = p2[0] - (p3[0] - p1[0]) / 6
        const c2y = p2[1] - (p3[1] - p1[1]) / 6

        d += `C${format(c1x)} ${format(c1y)} ${format(c2x)} ${format(c2y)} ${format(p2[0])} ${format(p2[1])}`
    }

    return `${d}Z`
}

/**
 * Builds organic, concentric topographic rings around each hill.
 * Uses trigonometry, so render the result on the client only.
 */
export const buildContours = (hills: Hill[], resolution = 22): string[] => {
    return hills.flatMap((hill) => {
        const rings: string[] = []

        for (let ring = 0; ring < hill.rings; ring++) {
            const t = 1 - ring / hill.rings
            const radius = hill.radius * (0.16 + 0.84 * t)
            const points: Point[] = []

            for (let i = 0; i < resolution; i++) {
                const angle = (i / resolution) * Math.PI * 2
                const wobble = 1
                    + 0.16 * Math.sin(3 * angle + hill.seed + ring * 0.35)
                    + 0.08 * Math.sin(5 * angle - hill.seed * 1.7 + ring * 0.2)
                    + 0.05 * Math.cos(7 * angle + hill.seed * 2.3)

                points.push([
                    hill.x + Math.cos(angle) * radius * wobble * (hill.stretch ?? 1.35),
                    hill.y + Math.sin(angle) * radius * wobble
                ])
            }

            rings.push(smoothClosedPath(points))
        }

        return rings
    })
}

/** Small deterministic random generator, safe to use during server rendering. */
export const seededRandom = (seed: number): (() => number) => {
    let state = seed

    return () => {
        state += 0x6d2b79f5
        let t = state
        t = Math.imul(t ^ (t >>> 15), t | 1)
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296
    }
}
